import { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { videos } from "@/mocks/videos";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [showSuggest, setShowSuggest] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const suggestions = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return [];
    return videos
      .filter(
        (v) =>
          v.title.toLowerCase().includes(q) ||
          v.tags.some((tag) => tag.toLowerCase().includes(q)) ||
          v.author.toLowerCase().includes(q),
      )
      .slice(0, 6);
  }, [keyword]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setShowSuggest(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const submitSearch = (value: string) => {
    const q = value.trim();
    if (!q) return;
    setShowSuggest(false);
    setMobileOpen(false);
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  const navItemClass = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
      isActive
        ? "text-primary-600 bg-primary-50"
        : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
    }`;

  return (
    <header className="sticky top-0 z-50 bg-background-50/95 backdrop-blur border-b border-background-200">
      <div className="flex items-center gap-4 h-16 px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-play-circle-fill text-xl" aria-hidden="true"></i>
          </span>
          <span className="font-heading font-extrabold text-lg text-foreground-950 hidden sm:block">
            呱呱视频
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1" aria-label="主导航">
          <NavLink to="/" className={navItemClass} end>
            首页
          </NavLink>
          {categories.map((cat) => (
            <NavLink key={cat.slug} to={`/category/${cat.slug}`} className={navItemClass}>
              {cat.name}
            </NavLink>
          ))}
        </nav>

        <div className="flex-1" />

        <div ref={boxRef} className="relative hidden md:block w-64 lg:w-80">
          <div className="flex items-center gap-2 h-10 px-3 rounded-full bg-background-100 border border-background-200 focus-within:border-primary-400">
            <i className="ri-search-line text-foreground-400" aria-hidden="true"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value);
                setShowSuggest(true);
              }}
              onFocus={() => setShowSuggest(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitSearch(keyword);
              }}
              placeholder="搜索视频、作者或标签"
              className="flex-1 bg-transparent outline-none text-sm text-foreground-900 placeholder:text-foreground-400"
            />
            {keyword && (
              <button
                type="button"
                onClick={() => setKeyword("")}
                className="w-5 h-5 flex items-center justify-center text-foreground-400 hover:text-foreground-700"
                aria-label="清空"
              >
                <i className="ri-close-line text-sm" aria-hidden="true"></i>
              </button>
            )}
          </div>
          {showSuggest && suggestions.length > 0 && (
            <div className="absolute mt-2 w-full rounded-lg border border-background-200 bg-background-50 py-1 animate-fade-in overflow-hidden">
              {suggestions.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => {
                    setShowSuggest(false);
                    setKeyword("");
                    navigate(`/video/${v.id}`);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-background-100 transition-colors"
                >
                  <i className="ri-play-circle-line text-primary-500" aria-hidden="true"></i>
                  <span className="text-sm text-foreground-800 truncate">{v.title}</span>
                </button>
              ))}
              <button
                type="button"
                onClick={() => submitSearch(keyword)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left border-t border-background-200 hover:bg-background-100 transition-colors"
              >
                <i className="ri-search-line text-foreground-400" aria-hidden="true"></i>
                <span className="text-sm text-foreground-600">搜索“{keyword}”</span>
              </button>
            </div>
          )}
        </div>

        <Link
          to="/upload"
          className="hidden sm:inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-upload-cloud-2-line" aria-hidden="true"></i>
          上传视频
        </Link>

        <Link
          to="/profile"
          className="hidden md:flex w-10 h-10 items-center justify-center rounded-full bg-background-100 text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer"
          aria-label="个人中心"
        >
          <i className="ri-user-line" aria-hidden="true"></i>
        </Link>

        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-800 hover:bg-background-100"
          aria-label="打开菜单"
        >
          <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} aria-hidden="true"></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 pb-4 pt-3 animate-fade-in">
          <div className="flex items-center gap-2 h-10 px-3 mb-3 rounded-full bg-background-100 border border-background-200">
            <i className="ri-search-line text-foreground-400" aria-hidden="true"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitSearch(keyword);
              }}
              placeholder="搜索视频、作者或标签"
              className="flex-1 bg-transparent outline-none text-sm"
            />
          </div>
          <nav className="flex flex-col" aria-label="移动端导航">
            <NavLink
              to="/"
              end
              onClick={() => setMobileOpen(false)}
              className="px-2 py-3 text-sm font-medium text-foreground-800 border-b border-background-100"
            >
              首页
            </NavLink>
            {categories.map((cat) => (
              <NavLink
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => setMobileOpen(false)}
                className="px-2 py-3 text-sm font-medium text-foreground-800 border-b border-background-100 flex items-center gap-2"
              >
                <i className={cat.icon} aria-hidden="true"></i>
                {cat.name}
              </NavLink>
            ))}
            <NavLink
              to="/upload"
              onClick={() => setMobileOpen(false)}
              className="px-2 py-3 text-sm font-medium text-foreground-800 border-b border-background-100"
            >
              上传视频
            </NavLink>
            <NavLink
              to="/profile"
              onClick={() => setMobileOpen(false)}
              className="px-2 py-3 text-sm font-medium text-foreground-800"
            >
              个人中心
            </NavLink>
          </nav>
        </div>
      )}
    </header>
  );
}